// BrowseOffersPanel — Phase 5 (2026-05-11).
//
// Refactor of AgentRewardsBrowse for the consolidated RewardsHub.
// The original page wrapped its body in AdminLayout (its own title +
// subtitle); the hub now owns the page-level chrome, so this panel
// renders ONLY the offer-card grid + hero + opt-in modal.
//
// Same backend, same data flow, same modal. The hub puts this under
// /rewards/browse and the legacy /rewards URL redirects here so all
// existing deep-links continue working.

import { useEffect, useState } from 'react';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import OfferCard, { OfferLike } from '@baaligames/shared-ui/components/OfferCard';
// Phase R7 follow-up (2026-05-13): OfferHero removed for the agent
// view — hero is a player-facing claim affordance; the agent's
// `/rewards/browse` is read-only opt-in management, so the hero added
// vertical noise without any actionable purpose. If a future
// "featured offer" prompt for agents becomes useful, re-import here.
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
import OfferOptInModal from '../../components/OfferOptInModal';

type AgentOfferRow = OfferLike & {
  am_opted_in: 0 | 1 | boolean;
  my_cost_share_pct: number | null;
};

export default function BrowseOffersPanel() {
  const t = useT();
  const [rows, setRows] = useState<AgentOfferRow[] | null>(null);
  const [err, setErr]   = useState<string | null>(null);
  const [activeModal, setActiveModal] = useState<AgentOfferRow | null>(null);

  async function refetch() {
    try {
      const r = await adminApi.get<{ offers: AgentOfferRow[] }>('/admin/me-agent/offers');
      setRows(r.offers);
    } catch (e) {
      setErr((e as ApiError).message || t('agent.rewards.browse.error.loadFailed'));
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<{ offers: AgentOfferRow[] }>(
          '/admin/me-agent/offers'
        );
        if (!cancelled) setRows(r.offers);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || t('agent.rewards.browse.error.loadFailed'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4">
      {err && (
        <div className="rounded-xl bg-rose-500/10 border border-rose-400/30
                        text-rose-700 dark:text-rose-200 text-sm px-4 py-3">
          {err}
        </div>
      )}

      {/* Loading — single-column placeholders match the post-Phase 16
          single-column grid below; on desktop the offer card has
          horizontal layout (icon + meta + action) and benefits from
          full container width rather than being squeezed into 1/3. */}
      {!rows && !err && (
        <div className="space-y-3 animate-pulse">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-28 rounded-2xl bg-arena-200/40 dark:bg-arena-700/40" />
          ))}
        </div>
      )}

      {rows && (
        <>
          {/* Phase R7 follow-up (2026-05-13): hero removed for agent view. */}

          {/* Empty state */}
          {rows.length === 0 && (
            <div className="rounded-2xl bg-white dark:bg-arena-700/40
                            border border-arena-200/60 dark:border-arena-600/50
                            p-10 text-center">
              <Icon name="hourglass_empty" size={32}
                    className="text-arena-500 dark:text-neon-200/60 mx-auto mb-2" />
              <p className="text-[13px] font-bold text-arena-700 dark:text-neon-200/85">
                {t('agent.rewards.browse.empty.title')}
              </p>
              <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1 max-w-sm mx-auto">
                {t('agent.rewards.browse.empty.body')}
              </p>
            </div>
          )}

          {/* Card grid — each card is a button that opens OfferOptInModal
              so the agent can manage their opt-in state + sub-agent
              rates inline. The opt-in chip is the visible affordance
              ("tap to manage") + state indicator in one.
              Phase 16 (2026-05-12): single-column stack on every
              breakpoint. The previous lg:grid-cols-3 squeezed each
              card into a square box where the offer title, reward
              amount, and CTA collided; on mobile the natural
              full-width horizontal layout was already working, so we
              keep that same shape across all sizes. */}
          {rows.length > 0 && (
            <div className="space-y-3">
              {rows.map((o) => (
                <div key={o.id} className="relative group">
                  {/* Whole card is a click target. */}
                  <button
                    type="button"
                    onClick={() => setActiveModal(o)}
                    className="absolute inset-0 z-20 rounded-2xl
                               focus:outline-none focus:ring-2 focus:ring-amber-400/50"
                    aria-label={t('agent.rewards.browse.card.aria.manage', { title: o.title })}
                  />
                  <OfferCard offer={o} />
                  {/* Opt-in chip — visual "tap to manage" affordance. */}
                  <div className="absolute top-3 left-3 z-10 pointer-events-none">
                    {o.am_opted_in ? (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                                       text-[10px] font-bold uppercase tracking-wider
                                       bg-emerald-500/90 text-white shadow
                                       group-hover:bg-emerald-400 transition">
                        <Icon name="check_circle" size={11} filled />
                        {t('agent.rewards.browse.chip.optedIn')}
                        {o.my_cost_share_pct != null && (
                          <span className="opacity-80">{t('agent.rewards.browse.chip.shareSuffix', { pct: o.my_cost_share_pct })}</span>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5
                                       text-[10px] font-bold uppercase tracking-wider
                                       bg-amber-500 text-white shadow
                                       group-hover:bg-amber-400 transition">
                        <Icon name="touch_app" size={11} filled />
                        {t('agent.rewards.browse.chip.optInPrompt')}
                      </span>
                    )}
                  </div>
                  {/* Phase R7 follow-up (2026-05-14): magenta "NEW"
                      pill mirrors the Rewards-tab badge in the bottom
                      nav. Same brand-500 magenta — when the agent
                      sees a "3" on the Rewards tab and taps in, the
                      three cards each wear this pill, so the visual
                      chain alert→cause is unbroken. Rendered only for
                      un-opted-in offers; once the agent opts in the
                      pill disappears AND the bottom-nav count drops
                      by one. Sits in the top-right so it doesn't
                      collide with the amber "tap to opt in" chip
                      (top-left). Subtle pulse to draw the eye
                      without competing with the amber chip's role
                      as the primary interaction affordance. */}
                  {!o.am_opted_in && (
                    <motion.span
                      initial={{ scale: 0.7, opacity: 0 }}
                      animate={{ scale: [1, 1.06, 1], opacity: 1 }}
                      transition={{
                        opacity: { duration: 0.2 },
                        scale:  { duration: 1.8, repeat: Infinity, ease: 'easeInOut' },
                      }}
                      className="absolute top-3 right-3 z-10 pointer-events-none
                                 inline-flex items-center justify-center
                                 h-[18px] min-w-[18px] px-2
                                 rounded-full
                                 text-[10px] font-extrabold uppercase tracking-wider
                                 bg-brand-500 text-white
                                 shadow-[0_1px_4px_rgba(233,30,140,0.55)]
                                 ring-[1.5px] ring-white dark:ring-arena-800"
                      aria-label={t('agent.offers.badge.newAria')}
                    >
                      {t('agent.offers.badge.new')}
                    </motion.span>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Phase 15.5 — Opt-in modal. Mounts when a card is tapped. */}
      {activeModal && (
        <OfferOptInModal
          offer={{
            id: activeModal.id,
            title: activeModal.title,
            image_url: activeModal.image_url ?? null,
            reward_kind: activeModal.reward_kind,
            reward_value: activeModal.reward_value,
            reward_cap: activeModal.reward_cap,
          }}
          onClose={() => setActiveModal(null)}
          onUpdated={refetch}
        />
      )}
    </div>
  );
}
